import React, { useState, useEffect, useCallback } from 'react';
import {
  Building2, CheckCircle2, AlertTriangle, XCircle, ChevronDown, Loader2, Landmark,
  Star, Wrench,
} from 'lucide-react';
import { loanProfilingApi } from '@/services/api';

const CHECK_STYLE = {
  pass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  soft: 'bg-amber-50 text-amber-700 border-amber-200',
  hard: 'bg-rose-50 text-rose-700 border-rose-200',
  unknown: 'bg-slate-50 text-slate-500 border-slate-200',
};

const STATUS = {
  STRAIGHT: { label: 'Straight — issue now', chip: 'bg-emerald-600', icon: CheckCircle2, color: 'text-emerald-600' },
  EDGE: { label: 'Edge — push offline', chip: 'bg-amber-500', icon: AlertTriangle, color: 'text-amber-600' },
  NO: { label: 'Not a fit', chip: 'bg-rose-600', icon: XCircle, color: 'text-slate-400' },
};

const GateChip = ({ c }) => (
  <span
    className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${CHECK_STYLE[c.status] || CHECK_STYLE.unknown}`}
    title={c.detail || ''}
    data-testid={`gate-${c.key}-${c.status}`}
  >
    {c.label}{c.status === 'pass' ? ' ✓' : c.detail ? `: ${c.detail}` : ''}
  </span>
);

const BankRow = ({ m, recommended }) => (
  <div
    className={`rounded-lg border p-3 ${recommended ? 'border-emerald-300 ring-1 ring-emerald-200 bg-emerald-50/40' : 'border-slate-200 bg-white'}`}
    data-testid={`bank-match-row-${m.bank_code}`}
  >
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-2 min-w-0">
        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
          <Landmark className="w-4 h-4 text-slate-500" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900 truncate flex items-center gap-1.5">
            {m.bank_name}
            {recommended && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase text-emerald-700 bg-emerald-100 border border-emerald-200 px-1.5 py-0.5 rounded"
                    data-testid="bank-match-recommended-badge">
                <Star className="w-2.5 h-2.5 fill-emerald-600 text-emerald-600" /> Best fit
              </span>
            )}
          </div>
          <div className="text-[11px] text-slate-400">
            {m.rate_min != null ? `${m.rate_min}%–${m.rate_max}% p.a.` : m.bank_code}
          </div>
        </div>
      </div>
      <div className="text-[11px] font-medium text-slate-400">{m.passed}/{m.total} rules pass</div>
    </div>
    <div className="flex flex-wrap gap-1.5 mt-2">
      {(m.checks || []).map((c) => <GateChip key={c.key} c={c} />)}
    </div>
    {(m.levers || []).length > 0 && (
      <div className="mt-2 pt-2 border-t border-dashed border-slate-200 space-y-1" data-testid={`bank-levers-${m.bank_code}`}>
        {m.levers.map((lv, i) => (
          <div key={i} className="flex items-start gap-1.5 text-xs text-slate-600">
            <Wrench className="w-3 h-3 mt-0.5 text-amber-500 shrink-0" /> {lv}
          </div>
        ))}
      </div>
    )}
  </div>
);

const Group = ({ title, icon: Icon, color, matches, testid, recommendedId, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  if (!matches.length) return null;
  return (
    <div data-testid={testid}>
      <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 w-full text-left py-2" data-testid={`${testid}-toggle`}>
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-sm font-semibold text-slate-700">{title}</span>
        <span className="text-xs font-medium text-slate-400">· {matches.length}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 ml-auto transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="space-y-2">
          {matches.map((m) => <BankRow key={m.bank_id} m={m} recommended={m.bank_id === recommendedId} />)}
        </div>
      )}
    </div>
  );
};

export const BankMatchPanel = ({ leadId, reloadKey }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { setLoading(true); const res = await loanProfilingApi.bankMatches(leadId); setData(res.data); }
    catch (e) { setData(null); } finally { setLoading(false); }
  }, [leadId]);

  useEffect(() => { load(); }, [load, reloadKey]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 text-sm p-6 border border-slate-200 rounded-xl mt-8" data-testid="bank-match-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Matching partner banks…
      </div>
    );
  }
  if (!data?.matches?.length) {
    return (
      <div className="mt-8 text-sm text-slate-500 border border-slate-200 rounded-xl p-4" data-testid="bank-match-empty">
        No active partner banks configured. Add banks in Settings → Bank Master to see which lender can fund this purchase.
      </div>
    );
  }

  const straight = data.matches.filter((m) => m.status === 'STRAIGHT');
  const edge = data.matches.filter((m) => m.status === 'EDGE');
  const no = data.matches.filter((m) => m.status === 'NO');
  const rec = data.recommended_bank;
  const recId = rec?.bank_id;

  return (
    <div className="mt-8" data-testid="bank-match-panel">
      <div className="flex items-center gap-2 mb-1">
        <Building2 className="w-4 h-4 text-slate-400" />
        <h4 className="text-sm font-semibold text-slate-700">Bank Match</h4>
        <span className="text-xs text-slate-400">— which lender can fund this used-car purchase</span>
      </div>

      {/* Recommended banner */}
      {rec && (
        <div
          className={`rounded-lg border p-3 mb-3 flex items-center gap-3 ${rec.status === 'STRAIGHT' ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`}
          data-testid="bank-match-recommended"
        >
          <Star className={`w-5 h-5 ${rec.status === 'STRAIGHT' ? 'text-emerald-600 fill-emerald-500' : 'text-amber-600 fill-amber-400'}`} />
          <div className="text-sm">
            <span className="font-semibold text-slate-900">Best fit: {rec.bank_name}</span>
            <span className="text-slate-600">
              {' '}{rec.rate_min != null ? `at ${rec.rate_min}% p.a. ` : ''}
              — {rec.status === 'STRAIGHT' ? 'clears now, issue without discussion' : 'closest — push offline to close the gap'}
            </span>
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="flex items-center gap-3 text-xs mb-3" data-testid="bank-match-summary">
        <span className="text-emerald-700 font-medium">{data.summary.straight} straight</span>
        <span className="text-amber-700 font-medium">{data.summary.edge} edge</span>
        <span className="text-slate-400">{data.summary.no} no-go</span>
      </div>

      {!data.has_bureau && (
        <div className="mb-3 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
          Bureau not pulled yet — matches use the captured/declared data; pull CIBIL for an exact ranking.
        </div>
      )}

      {/* What we can do — lead-level levers */}
      {(data.recommended_actions || []).length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-3 mb-3" data-testid="bank-match-actions">
          <div className="flex items-center gap-1.5 mb-2">
            <Wrench className="w-3.5 h-3.5 text-amber-500" />
            <span className="text-sm font-semibold text-slate-700">What we can do to get this funded</span>
          </div>
          <ul className="space-y-1">
            {data.recommended_actions.map((a, i) => (
              <li key={i} className="text-xs text-slate-600 flex items-start gap-1.5">
                <span className="text-amber-500 mt-0.5">→</span> {a}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-3">
        <Group title="Straight — issue now" icon={CheckCircle2} color="text-emerald-600" matches={straight} testid="bank-match-straight" recommendedId={recId} />
        <Group title="Edge — push offline" icon={AlertTriangle} color="text-amber-600" matches={edge} testid="bank-match-edge" recommendedId={recId} />
        <Group title="Not a fit" icon={XCircle} color="text-slate-400" matches={no} testid="bank-match-no" recommendedId={recId} defaultOpen={false} />
      </div>
    </div>
  );
};

export default BankMatchPanel;
